// Grids & Gantts — External database columns, the PURE half (no imports, so
// Node suites load it as it is; see the fork-suite rule in ../../README.md).
//
// A lookup column says: take this work item's field (the column's `field`),
// find the row of an External Database Source's table whose `match` column
// equals it, show that row's `value` column. The page asks the source ONCE
// for every distinct value the grid holds (useLookups.js) and keeps the
// answers here:
//
//   store[groupKey] = { values: Map(asked value → row object | null), error }
//
// One group per (source, table, match, value) — a column showing a second
// column of the same table asks for it on its own, so an answer never lacks
// the column a cell reads.
//
// ADD UP EVERY MATCHING ROW (lookup.sum). A part in an SAP stock export is
// one row per storage location — 1473_NB is 1 070 in store 1000, 64.739 in
// 2700 and nought in five more — so "the first row" is one shelf of seven.
// A summing column asks for EVERY row that matches and shows the total of the
// shown column's figures ("1,070.000" read as 1070); no matching row at all
// is 0, not a blank, because that is what is on the shelf.
//
// PER VALUE OF A COLUMN (lookup.per). The same rows added up per value of a
// second column — per storage location: "1000: 1,126 · 2700: 50.698". A
// location holding nothing is left out; rows that all hold nothing read 0.

export const isLookup = (c) => !!(c && c.kind === 'lookup' && c.lookup && c.lookup.source && c.lookup.match && c.lookup.value);

export const groupKeyOf = (lk) => JSON.stringify([String(lk.source || '').toLowerCase(), lk.table || '', lk.match, lk.value, ...(lk.sum ? ['sum'] : []), ...(lk.per ? ['per', lk.per] : [])]);

// What to ask for: per group, the distinct first values of the column's field
// over the loaded rows. `textsOf(item, fieldId)` is handed in (it lives in
// columns.js — this file imports nothing).
export function lookupPlan(cols, fieldIds, items, textsOf) {
  const plan = new Map();
  for (const c of cols || []) {
    if (!isLookup(c)) continue;
    const fid = (fieldIds || {})[c.id];
    if (!fid) continue;
    const gk = groupKeyOf(c.lookup);
    let g = plan.get(gk);
    if (!g) { g = { key: gk, source: c.lookup.source, table: c.lookup.table || null, match: c.lookup.match, value: c.lookup.value, sum: !!c.lookup.sum, per: c.lookup.per || '', values: new Set() }; plan.set(gk, g); }
    if (items) {
      items.forEach((it) => {
        const v = textsOf(it, fid)[0];
        if (v != null && v !== '') g.values.add(String(v));
      });
    }
  }
  return plan;
}

// The values of a group the store has not answered and nobody is asking. A
// group that was refused is not asked again until the next load (↻) — a
// source with no access must not be hammered on every render.
export function missingValues(group, entry, inFlight) {
  if (entry && entry.error) return [];
  const out = [];
  group.values.forEach((v) => {
    if (entry && entry.values && entry.values.has(v)) return;
    if (inFlight && inFlight.has(v)) return;
    out.push(v);
  });
  return out;
}

// A figure as an export writes it: "33.000", "1,070.000", "1 070,5", "5.000-"
// (SAP's trailing minus). null when the text is not a figure. (./lines.js
// reads figures the same way; each file keeps its own copy — no imports here.)
export function figureOf(text) {
  if (typeof text === 'number') return Number.isFinite(text) ? text : null;
  let s = String(text == null ? '' : text).trim().replace(/ /g, ' ');
  if (!s) return null;
  let neg = false;
  if (/-$/.test(s)) { neg = true; s = s.slice(0, -1).trim(); }
  if (/^-/.test(s)) { neg = !neg; s = s.slice(1).trim(); }
  if (!/^[\d.,' ]+$/.test(s)) return null;
  s = s.replace(/[' ]/g, '');
  const dot = s.lastIndexOf('.'); const comma = s.lastIndexOf(',');
  if (dot >= 0 && comma >= 0) s = dot > comma ? s.replace(/,/g, '') : s.replace(/\./g, '').replace(',', '.');
  else if (comma >= 0) s = /^\d{1,3}(,\d{3})+$/.test(s) ? s.replace(/,/g, '') : s.replace(',', '.');
  else if ((s.match(/\./g) || []).length > 1) s = s.replace(/\./g, '');
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  return neg ? -n : n;
}

// The total of one column over the rows a summing lookup answered.
export function sumOfRows(rows, column) {
  let total = 0;
  (Array.isArray(rows) ? rows : (rows ? [rows] : [])).forEach((r) => {
    if (!r || typeof r !== 'object') return;
    const k = Object.prototype.hasOwnProperty.call(r, column) ? column : Object.keys(r)[0];
    const n = k === undefined ? null : figureOf(r[k]);
    if (n != null) total += n;
  });
  return Math.round(total * 1000) / 1000;
}

// The rows added up per value of another column, in the order of that
// value (numbers as numbers): [{ at: '1000', qty: 1126 }, …] — zeros left out.
export function perOfRows(rows, column, per) {
  const by = new Map();
  (Array.isArray(rows) ? rows : (rows ? [rows] : [])).forEach((r) => {
    if (!r || typeof r !== 'object') return;
    const n = figureOf(r[column]);
    if (n == null) return;
    const at = String(r[per] == null ? '' : r[per]).trim() || '(blank)';
    by.set(at, (by.get(at) || 0) + n);
  });
  return [...by.entries()].map(([at, qty]) => ({ at, qty: Math.round(qty * 1000) / 1000 })).filter((x) => x.qty !== 0)
    .sort((a, b) => a.at.localeCompare(b.at, undefined, { numeric: true }));
}
const fig = (n) => n.toLocaleString('en-US', { maximumFractionDigits: 3 });

// A cell's answer: undefined while asking, null for no row, { value } or { error }.
// A summing column answers { value: total, rows: n } — and 0 for no row; a
// per-column one { value: "1000: 1,126 · 2700: 50.698", per: [...] }.
export function lookupCell(store, col, value) {
  if (!isLookup(col)) return null;
  const entry = store && store[groupKeyOf(col.lookup)];
  if (!entry) return undefined;
  const v = String(value);
  if (entry.values && entry.values.has(v)) {
    const row = entry.values.get(v);
    if (col.lookup.per) {
      const rows = Array.isArray(row) ? row : (row ? [row] : []);
      if (!rows.length) return null;
      const per = perOfRows(rows, col.lookup.value, col.lookup.per);
      return { value: per.length ? per.map((x) => `${x.at}: ${fig(x.qty)}`).join(' · ') : '0', per };
    }
    if (col.lookup.sum) {
      const rows = Array.isArray(row) ? row : (row ? [row] : []);
      return { value: sumOfRows(rows, col.lookup.value), rows: rows.length };
    }
    if (!row) return null;
    const k = Object.prototype.hasOwnProperty.call(row, col.lookup.value) ? col.lookup.value : Object.keys(row)[0];
    return { value: k === undefined ? null : row[k] };
  }
  if (entry.error) return { error: entry.error };
  return undefined;
}

// Answers merged into a group's entry (a new Map — the store is never mutated).
export function withAnswers(entry, asked, answered) {
  const values = new Map(entry && entry.values ? entry.values : []);
  for (const v of asked) values.set(v, answered && Object.prototype.hasOwnProperty.call(answered, v) ? answered[v] : null);
  return { values, error: null };
}

export const chunk = (list, n) => { const out = []; for (let i = 0; i < list.length; i += n) out.push(list.slice(i, i + n)); return out; };
